// src/pages/PreLoginSplashPage.js
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import { PlusCircle } from "lucide-react";
import Modal from "../components/Modal";
import logoLight from "../assets/light_logo.jpg";
import darkGrBg from "../assets/dark_gr_bg.jpg";
import darkBg from "../assets/dark_bg.jpg";

function safeParseUser() {
  try {
    return JSON.parse(sessionStorage.getItem("auth_user") || "null");
  } catch {
    return null;
  }
}

export default function PreLoginSplashPage() {
  const nav = useNavigate();

  const [dbs, setDbs] = useState([]);
  const [users, setUsers] = useState([]);

  const [dbId, setDbId] = useState(sessionStorage.getItem("active_db") || "");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");

  const [newDbName, setNewDbName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);


  // auth state (fixes eslint: setAuthUser / safeParseUser)
  const [authUser, setAuthUser] = useState(() => safeParseUser());
  const [animatingOut, setAnimatingOut] = useState(false);
  const animTimerRef = useRef(null);

  // Force-password-change flow. When login returns must_change_password we
  // stash the temp password + freshly-issued JWT, show the ForceChange modal,
  // and only route to /home once the user has picked a permanent password.
  const [forceChange, setForceChange] = useState(null);
    // { token, user, tempPassword }

  const theme = localStorage.getItem("sna_theme") || "system";
  const bgImg = useMemo(() => {
    if (theme === "dark") return darkGrBg;
    if (theme === "light") return logoLight;
    return darkBg;
  }, [theme]);

  // keep auth state synced (same-tab + other tabs)
  useEffect(() => {
    setAuthUser(safeParseUser());

    const onStorage = (e) => {
      if (e.key === "auth_user" || e.key === "auth_token") {
        setAuthUser(safeParseUser());
      }
    };
    window.addEventListener("storage", onStorage);

    // same-tab updates: poll for changes (cheap + reliable)
    let last = sessionStorage.getItem("auth_user") || "";
    const t = setInterval(() => {
      const cur = sessionStorage.getItem("auth_user") || "";
      if (cur !== last) {
        last = cur;
        setAuthUser(safeParseUser());
      }
    }, 500);

    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(t);
    };
  }, []);

  // if already logged in, skip splash
  useEffect(() => {
    const tok = sessionStorage.getItem("auth_token") || "";
    if (tok) nav("/home", { replace: true });
  }, [nav, authUser]);

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const [a, b] = await Promise.all([api.get("/public/dbs"), api.get("/public/users")]);
      const dbRows = Array.isArray(a.data) ? a.data : [];
      const userRows = Array.isArray(b.data) ? b.data : [];
      setDbs(dbRows);
      setUsers(userRows);

      if (!dbId && dbRows[0]?.dbId) setDbId(dbRows[0].dbId);
      if (!userId && userRows[0]?.id) setUserId(String(userRows[0].id));
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load login data");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const login = async () => {
    if (!dbId) return setMsg("Select a database");
    if (!userId) return setMsg("Select a user");
    if (!password) return setMsg("Enter password");

    setBusy(true);
    setMsg("");
    try {
      sessionStorage.setItem("active_db", dbId);

      const u = users.find((x) => String(x.id) === String(userId));
      if (!u?.email) throw new Error("Selected user missing email");

      const { data } = await api.post("/auth/login", { email: u.email, password });

      const token = data?.token || "";
      if (!token) throw new Error("Login returned no token");

      // Forced-reset path: don't persist the session or route away. Show the
      // change-password modal first; routing happens when the user commits
      // a new password. This prevents them from bypassing the reset by
      // just clicking around.
      if (data?.must_change_password || data?.user?.must_change_password) {
        setForceChange({ token, user: data.user || null, tempPassword: password });
        return;
      }

      sessionStorage.setItem("auth_token", token);

      // IMPORTANT: persist user so NavBar can show display name immediately
      if (data?.user) {
        sessionStorage.setItem("auth_user", JSON.stringify(data.user));
        setAuthUser(data.user);
      } else {
        // fallback: keep auth_user consistent (avoid stale name)
        sessionStorage.removeItem("auth_user");
        setAuthUser(null);
      }

      // pre-login splash slides up before routing
      setAnimatingOut(true);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      animTimerRef.current = setTimeout(() => {
        nav("/home", { replace: true });
      }, 420);
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.message || "Login failed");
      setAnimatingOut(false);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    };
  }, []);

  const createDb = async () => {
    const name = String(newDbName || "").trim();
    if (!name) return;

    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/db/create", {
        name,
        template: "inventory",
        extras: { finance: true, purchase_orders: true, tracking: true, date_tracking: true },
      });

      const createdDbId = data?.dbId;
      if (!createdDbId) throw new Error("DB created but no dbId returned");

      await load();
      setDbId(createdDbId);
      setNewDbName("");
      setMsg("Database created. Select it and login.");
      setModalOpen(false);
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.message || "Failed to create database");
    } finally {
      setBusy(false);
    }
  };


  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {/* blurred background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${bgImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(8px)",
          transform: animatingOut ? "translateY(-110%) scale(1.1)" : "scale(1.05)",
          transition: "transform 420ms ease",
        }}
      />
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} />

      {/* card */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 420,
          margin: "0 16px",
          transform: animatingOut ? "translateY(-110%)" : "translateY(0)",
          transition: "transform 420ms ease",
        }}
      >
        {/* main login card */}
        <div
          style={{
            background: "rgba(245,243,240,0.97)",
            borderRadius: 20,
            padding: "36px 32px 28px",
            boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
          }}
        >
          <div style={{ fontWeight: 900, fontSize: 38, color: "#111", letterSpacing: -0.5, marginBottom: 4 }}>
            Login
          </div>
          <div style={{ color: "#888", fontSize: 15, marginBottom: 24 }}>Select user + password</div>

          {msg && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 14, marginBottom: 16 }}>
              {msg}
            </div>
          )}

          <div style={{ display: "grid", gap: 12 }}>
            {/* DB selector — compact row */}
            {dbs.length > 1 && (
              <div>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 5, fontWeight: 600 }}>Database</div>
                <select
                  value={dbId}
                  onChange={(e) => setDbId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "13px 14px",
                    fontSize: 15,
                    border: "1.5px solid #e2e2e2",
                    borderRadius: 10,
                    background: "#fff",
                    color: "#111",
                    outline: "none",
                    appearance: "auto",
                  }}
                >
                  <option value="">Select database…</option>
                  {dbs.map((d) => (
                    <option key={d.dbId} value={d.dbId}>{d.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* User selector */}
            <div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 5, fontWeight: 600 }}>User</div>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "13px 14px",
                  fontSize: 15,
                  border: "none",
                  borderRadius: 10,
                  background: "#2d3748",
                  color: "#fff",
                  outline: "none",
                  appearance: "auto",
                }}
              >
                <option value="">Select User</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name || u.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Password */}
            <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && login()}
                style={{
                  width: "100%",
                  padding: "13px 14px",
                  fontSize: 15,
                  border: "1.5px solid #e2e2e2",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#111",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Login button */}
            <button
              onClick={login}
              disabled={busy || animatingOut}
              style={{
                width: "100%",
                padding: "15px 0",
                fontSize: 17,
                fontWeight: 800,
                border: "none",
                borderRadius: 10,
                background: busy ? "#c4a0a0" : "#b07070",
                color: "#fff",
                cursor: busy ? "default" : "pointer",
                letterSpacing: 0.3,
                marginTop: 4,
              }}
            >
              {busy ? "Signing in…" : "Login"}
            </button>

            {/* Driver Portal button */}
            <button
              onClick={() => nav("/driver-portal")}
              style={{
                width: "100%",
                padding: "13px 0",
                fontSize: 15,
                fontWeight: 700,
                border: "1.5px solid #2d3748",
                borderRadius: 10,
                background: "transparent",
                color: "#2d3748",
                cursor: "pointer",
                letterSpacing: 0.2,
              }}
            >
              Driver Portal →
            </button>
          </div>
        </div>

        {/* bottom row: create DB + branding */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, padding: "0 4px" }}>
          <button
            onClick={() => setModalOpen(true)}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          >
            <PlusCircle size={14} /> New Database
          </button>
          <a
            href="https://almagestlabs.com/"
            target="_blank"
            rel="noreferrer"
            style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, textDecoration: "none", fontWeight: 700 }}
          >
            Almagest Labs ↗
          </a>
        </div>
      </div>

      {modalOpen && (
        <Modal onClose={() => setModalOpen(false)}>
          <h2>Create New Database</h2>
          <input
            className="input"
            placeholder="Database name (e.g. SNA 2026)"
            value={newDbName}
            onChange={(e) => setNewDbName(e.target.value)}
            style={{ width: "100%", marginBottom: 10 }}
          />
          <button className="btn" onClick={createDb} disabled={busy || !newDbName.trim()}>
            Create
          </button>
        </Modal>
      )}

      {forceChange && (
        <ForceChangePasswordModal
          authToken={forceChange.token}
          user={forceChange.user}
          tempPassword={forceChange.tempPassword}
          onDone={(fresh) => {
            // Commit the session + route to /home, now that the user has
            // picked a permanent password. We use the fresh token the
            // change-password endpoint handed back so the clock starts from
            // the successful reset.
            const token = fresh?.token || forceChange.token;
            const user  = fresh?.user  || forceChange.user || null;
            sessionStorage.setItem("auth_token", token);
            if (user) {
              sessionStorage.setItem("auth_user", JSON.stringify(user));
              setAuthUser(user);
            }
            setForceChange(null);
            setAnimatingOut(true);
            nav("/home", { replace: true });
          }}
          onCancel={() => {
            // User bailed — wipe the modal state; they'll have to log in
            // again. We never persisted the token so there's nothing to
            // clear from storage.
            setForceChange(null);
          }}
        />
      )}
    </div>
  );
}

// ── Force-change-password modal ───────────────────────────────────────────
// Shown immediately after a successful login when the server reports
// must_change_password. The user cannot dismiss their way past this — the
// token was issued but is not stored until they either change the password
// OR explicitly cancel (which drops them back to the login screen).
function ForceChangePasswordModal({ authToken, user, tempPassword, onDone, onCancel }) {
  const [newPw, setNewPw]       = useState("");
  const [confirm, setConfirm]   = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");

  const submit = async () => {
    setErr("");
    if (newPw.length < 8)   { setErr("New password must be at least 8 characters."); return; }
    if (newPw !== confirm)  { setErr("Passwords don't match."); return; }
    if (newPw === tempPassword) { setErr("Pick something different from the temporary password."); return; }

    setBusy(true);
    try {
      const { data } = await api.post(
        "/auth/change-password",
        { current_password: tempPassword, new_password: newPw },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      onDone?.(data);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Password change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onCancel}>
      <h2 style={{ marginTop: 0, color: "#1d4ed8" }}>🔑 Set your password</h2>
      <div className="muted" style={{ fontSize: 14, marginBottom: 12, lineHeight: 1.5 }}>
        {user?.display_name ? `Welcome, ${user.display_name}. ` : ""}
        Your account is using a temporary password. Pick a new one to finish signing in.
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 4 }}>
            New password
          </div>
          <input
            className="input"
            type="password"
            autoFocus
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="At least 8 characters"
            style={{ width: "100%", padding: 10, fontSize: 15, boxSizing: "border-box" }}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 4 }}>
            Confirm new password
          </div>
          <input
            className="input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type it again"
            style={{ width: "100%", padding: 10, fontSize: 15, boxSizing: "border-box" }}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
        </div>
      </div>

      {err && (
        <div style={{ marginTop: 10, color: "#dc2626", fontSize: 13, fontWeight: 600 }}>
          {err}
        </div>
      )}

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button className="btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn" onClick={submit} disabled={busy || !newPw || !confirm}>
          {busy ? "Saving…" : "Set Password & Continue"}
        </button>
      </div>
    </Modal>
  );
}
